import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, message } from 'antd';

const OTPVerificationModal = ({ visible, onCancel, email, onOTPVerified }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (visible) {
      // Clear OTP when modal opens
      setOtp(['', '', '', '', '', '']);
      // Focus first input
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 100);
    }
  }, [visible]);

  const handleInputChange = (index, value) => {
    if (value.length > 1) return; // Prevent multiple characters

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newOtp[i] = pastedData[i];
    }
    
    setOtp(newOtp);
    
    // Focus the next empty input or the last one
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleSubmit = async () => {
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      message.error('Please enter the complete 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://smtp-blush.vercel.app/api/auth/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          otp: otpString
        })
      });

      const data = await response.json();

      if (response.ok || data.message === 'OTP verified successfully') {
        message.success('OTP verified successfully!');
        onOTPVerified(email);
      } else {
        message.error(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      message.error('Failed to verify OTP. Please try again.');
      console.error('OTP verification error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setOtp(['', '', '', '', '', '']);
    onCancel();
  };

  return (
    <Modal
      title="Verify OTP"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button className='bg-primary text-white' key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Verify OTP
        </Button>,
      ]}
      centered
    >
      <div className="space-y-4">
        <p className="text-gray-600">
          We've sent a 6-digit OTP to <strong>{email}</strong>. Please enter it below.
        </p>
        
        <div className="flex justify-center space-x-2 gap-5">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="1"
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className="w-12 h-12 text-center text-xl font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          ))}
        </div>
        
        <p className="text-sm text-gray-500 text-center">
          You can paste the entire OTP code
        </p>
      </div>
    </Modal>
  );
};

export default OTPVerificationModal;
